import { Alert, Anchor, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { formatDayMonth } from '../helpers/dateUtils';
import useFetch from '../helpers/useFetch';

const REASON_COPY = {
  domainBlock:
    'Email sending is paused: receiving mail servers are rejecting your messages because your sending domain is on a blocklist. Sending resumes automatically once the rejections stop and the domain is clear.',
  listQuality:
    'Email sending is paused: more than 10% of the emails sent in the last 7 days went to addresses that do not exist, which usually means an outdated or purchased contact list. Remove the invalid addresses from your list, then contact support to resume sending.',
  sendingHealth:
    'Email sending is paused: of the emails sent in the last 3 days, 5% or more bounced as undeliverable or 0.3% or more were marked as spam by the people who received them, which usually follows a send to an old or unengaged list. Send only to contacts who have engaged recently, then contact support to resume sending.',
};

const hasCopy = (hold) =>
  hold.reason === 'blocklist' ? hold.hosts?.length > 0 : Boolean(REASON_COPY[hold.reason]);

const blocklistLines = (hold) => {
  const since = formatDayMonth(hold.since, 'local');
  return hold.hosts.map((host) => (
    <Text key={host.host} size="sm">
      Email sending is paused: your sending domain {host.host} is listed on Spamhaus DBL ({host.rbl})
      {since ? ` since ${since}` : ''}.{' '}
      <Anchor href={host.delist} target="_blank" rel="noopener noreferrer" fw={500}>
        Request removal
      </Anchor>
      , then sending resumes automatically within a few hours of the listing clearing.
    </Text>
  ));
};

// Only holds the API reports as active AND enforced come back here, so anything
// rendered is mail that is actually stopped right now. The response is advisory for
// the page around it: a failed request leaves that page usable rather than
// replacing it with an error.
const EmailSendingHoldAlert = ({ accountId, domainId, reloadToken }) => {
  const fetch = useFetch();
  const [holds, setHolds] = useState([]);
  // A Klaviyo-connected domain's mail leaves Klaviyo's infrastructure and is never
  // held by Releva, so there is nothing to ask about.
  const klaviyoAccount = useSelector((state) => state.klaviyo.account);
  const klaviyoConnected = Boolean(klaviyoAccount && !klaviyoAccount.credentialsInvalid);

  useEffect(() => {
    if (klaviyoConnected) {
      return;
    }
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/emailSendingHolds`)
      .then((response) => setHolds(response.holds ?? []))
      .catch(() => setHolds([]));
  }, [accountId, domainId, fetch, klaviyoConnected, reloadToken]);

  if (klaviyoConnected || !holds.some(hasCopy)) {
    return null;
  }

  return (
    <Stack gap="xs" mb="md">
      {holds.filter(hasCopy).map((hold) => (
        <Alert key={hold.reason} color="red" icon={<IconAlertTriangle size={18} />}>
          {hold.reason === 'blocklist' ? blocklistLines(hold) : <Text size="sm">{REASON_COPY[hold.reason]}</Text>}
        </Alert>
      ))}
    </Stack>
  );
};

export default EmailSendingHoldAlert;
